import React, { createContext, useState, useEffect, useContext } from "react";
import { ConfigContext } from "../security/ConfigContext";
import useSecurity from "../hooks/useSecurity";
import UserUtils from "../utils/UserUtils";
import Summaryutils from "../utils/SummaryUtils";
import JobUtils from "../utils/JobsUtils";
import ConfigUtils from "../utils/ConfigUtils";
import _ from "lodash";
import LoadingSpinner from "../components/common/LoadingSpinner";
import useAxios from "../adapter/useAxios";
import { Types } from "../constants/JobActions";
import {UserContext} from "./UserContext";
import { useHistory } from "react-router-dom";
import { SocketContext } from "./SocketContext";
import SocketUtils from "../utils/SocketUtils";
import { getPageTitle, Morph } from "../constants/Text";


const initialState = {
  job: {},
  setJob: (_job) => {},
onNotesAdded: (_noteRequestData) => {},
  sourceHeaders: [],
  deliveryHeaders: [],
  processorHeaders: [],
  onJobAction: (_action, _data) => {},
  onJobUpdated: (
    _isExternal,
    _requestData,
    _addNotes,
    _isWorkflowCancelled: boolean = false,
    _qcValidated: boolean = false,
    _beginQc: boolean = false,
    _isHoldFlow: boolean = false,
    _qcCallback?: () => void
  ) => {},
};

export const SummaryContext = createContext(initialState);

let jobId = null;

export const SummaryContextProvider = ({ children }) => {
  const jobTypes = ConfigUtils.getJobTypes();
  const [config] = useContext(ConfigContext);
  const { isAuthenticated, getAccessToken, userInfo } = useSecurity();
  const [job, setJob] = useState(initialState.job);
  const [sourceHeaders, setSourceHeaders] = useState([]);
  const [deliveryHeaders, setDeliveryHeaders] = useState([]);
  const [processorHeaders, setProcessor] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userRole,isUserLoading } = useContext(UserContext);
  const history = useHistory();
  const {get, put} = useAxios();
  const {_setMessageToSocket, messageFromSocket, _setMessageFromSocket} = useContext(SocketContext)
  const updateJobSocketMessage = SocketUtils.buildSocketMessage('JOB_SUMMARY');

  const populateJob = (jobData) => {
    try {
      const jobType = _.get(jobData.requests[0], "jobType");
      const idType = _.get(jobData.requests[0], "sourceType");
      const delivery = _.get(jobData, "destinations[0]");
      const processor = _.get(jobData, "processor");
      const selectedJobType = _.find(jobTypes, (type) => {
        return _.get(type, "jobType") === jobType;
      });
      const sourceDefinition = _.get(selectedJobType, "sourceDefinition");
      const sourceLocation = _.find(sourceDefinition, (sourceLoc) => {
        return _.get(sourceLoc, "type") === idType;
      });
      setSourceHeaders(Summaryutils.getHeaders({
        metadata: _.get(sourceLocation, "metadata"),
        overrideMetadata: _.get(sourceLocation && sourceLocation.override, "metadata"),
        readOnlyMetadata: _.get(sourceLocation, "readOnlyMetadata"),
        overrideReadOnlyMetadata: _.get(sourceLocation && sourceLocation.override, "readOnlyMetadata")
      }, _.get(jobData, "source")));

      let processingHeaders = Summaryutils.getProcessorHeaders(jobData, JobUtils.updateProcess({...processor}));

      setProcessor(processingHeaders);
      setDeliveryHeaders(Summaryutils.getDeliveryHeaders(delivery));
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    let jobIdValue = _.get(job, "jobId")
    if (jobIdValue) {
      jobIdValue = jobIdValue.replace("_v3", "");
      document.title = getPageTitle(jobIdValue, Morph.FOUNDRY);
    }
    // Reset title on unmount
    return () => {
      document.title = getPageTitle(Morph.MORPH, Morph.FOUNDRY);
    }
  }, [job]);

  const fetchData = (jobIdValue) => {
    get({
      url: config.apiUrls.jobs + jobIdValue,
      signal: true
    })
    .then((response) => {
      if (UserUtils.isJobTypeAccessible(userRole, _.get(response, 'jobType'))) {
        setJob(response);
        populateJob(response);
      } else {
        history.push("/");
      }
    })
    .catch((error) => {
      console.log(error)
    })
    .finally(() => {
      setLoading(false);
    }) 
  };

  useEffect(() => {
    if(messageFromSocket && SocketUtils.isDashboardAndSummaryRefresh(messageFromSocket)){
      console.log('Refreshed..')
      fetchData(jobId);
      _setMessageFromSocket("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageFromSocket]);

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    jobId = urlParams.get("id");
    setLoading(true);
    if (isAuthenticated) { 
      fetchData(jobId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, getAccessToken]);

  const onJobUpdated = (
    isExternal,
    requestData,
    addNotes,
    isWorkflowCancelled: boolean = false,
    qcValidated: boolean = false,
    beginQc: boolean = false,
    isHoldFlow: boolean = false,
    qcCallback?
  ) => {
    const updateJob = () => {
      requestData["user"] = UserUtils.transformUserInfo(userInfo, true);
      setLoading(true);
      if (isExternal) {
        return put({
          url: config.apiUrls.jobs + _.get(job, "_id") + "/status",
          data: requestData
        })
        .then(() => {
          _setMessageToSocket(updateJobSocketMessage);
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          setLoading(false);
        }) 
      } else if (isWorkflowCancelled || isHoldFlow) {
        let automationUrl = config.apiUrls.jobs + _.get(job, "_id") + "/status";
        const automationCall = (response?) => {
          return put({
            url: automationUrl,
            data: {
              status: isHoldFlow ? "hold" : "cancel",
              user: UserUtils.transformUserInfo(userInfo, true)
            }
          })
          .then(() => {
            _setMessageToSocket(updateJobSocketMessage);
            if(response){
              setJob(response[0] ? response[0] : response);
            }
          })
          .catch((error) => {
            console.log(error)
          }) 
          .finally(() => {
            setLoading(false);
          })
        };
        automationCall();
      } else {
        let url = addNotes ? config.apiUrls.jobs + _.get(job, "_id") + "/notes" : config.apiUrls.jobs + _.get(job, "_id");
        return put({
          url: url,
          data: requestData
        })
        .then((response) => {
          if (qcValidated) {
            let queryData = {};
            queryData["status"] = "WF_QC_DONE";
            queryData["user"] = UserUtils.transformUserInfo(userInfo, true);
            onJobUpdated(false, queryData, false, false);
          } else {
            if (beginQc) {
              if (qcCallback) {
                qcCallback();
              }
            }
            setJob(response[0] ? response[0] : response);
            _setMessageToSocket(updateJobSocketMessage);
          }
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          setLoading(false);
        })
      }
    };
    return updateJob();
  };

  const onNotesAdded = (noteRequestData) => {
    onJobUpdated(false, noteRequestData, true);
  };

  const onJobAction = (action, data) => {
    if (action === Types.RETRY || action === Types.CANCEL) {
      const requestData = {
        type: action,
        id: _.get(job, "_id"),
        data: data,
        user: UserUtils.transformUserInfo(userInfo, true)
      }
      setLoading(true);
      return put({
        url: config.apiUrls.jobsAction,
        data: requestData
      })
      .then(() => {
        _setMessageToSocket(updateJobSocketMessage);
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        setLoading(false);
      })
    }
  };

  return (
    <SummaryContext.Provider
      value={{
        job,
        setJob,
        onNotesAdded,
        sourceHeaders,
        deliveryHeaders,
        processorHeaders,
        onJobUpdated,
        onJobAction
      }}>
      {children}
      <LoadingSpinner show={loading || isUserLoading} />
    </SummaryContext.Provider>
  );
};
